import React, { useState, useRef, useMemo, useCallback, useImperativeHandle } from 'react';
import MentionEntry from './MentionEntry';
import { Box, TextField, Typography } from '@material-ui/core';
import { convertFromRaw, convertToRaw } from 'draft-js';
import Editor from '@draft-js-plugins/editor';
import createMentionPlugin from '@draft-js-plugins/mention';
import { createFromText } from 'draft-js/lib/ContentState';
import { createWithContent, push } from 'draft-js/lib/EditorState';
import { insertText, removeRange, replaceText } from 'draft-js/lib/DraftModifier';
import SelectionState from 'draft-js/lib/SelectionState';
import { mentionStyle } from './MentionStyles';
import DraftField from './DraftField';

const DraftEditor = ({
  value,
  valueMeta,
  innerRef,
  inputProps: originalInputInputProps,
  InputProps: originalInputProps,
  onChange,
  onKeyUp,
  multiline = false,
  mentionsList,
  mentionHelperText,
  ...selectiveProps
}) => {
  const [editorState, setEditorState] = useState(() => {
    if (valueMeta) {
      return createWithContent(convertFromRaw(valueMeta));
    }

    return createWithContent(createFromText(value));
  });

  const editorRef = useRef(null);

  const customSuggestionsFilter = (searchValue, suggestions) => {
    const size = (list) => list.constructor.name === 'List' ? list.size : list.length;
    const get = (obj, attr) => obj.get ? obj.get(attr) : obj[attr];
    const value = searchValue.toLowerCase();
    const filteredSuggestions = suggestions.filter((suggestion) => !value || get(suggestion, 'name').toLowerCase().indexOf(value) > -1);
    const length = size(filteredSuggestions);

    return filteredSuggestions.slice(0, length);
  };

  const handleOnChange = useCallback((newEditorState) => {
    setEditorState(newEditorState);
    if (onChange) {
      const text = newEditorState.getCurrentContent().getPlainText('');
      onChange({
        target: {
          value: text,
          meta: convertToRaw(newEditorState.getCurrentContent()),
          // aka get js object
        },
      });
    }
  }, [onChange]);

  useImperativeHandle(innerRef, () => {
    return {
      // ref.current.clear() will clear the textfield
      clear() {
        const clearEditorContent = (es) => {
          const blocks = es.getCurrentContent().getBlockMap().toList();
          const updatedSelection = es.getSelection().merge({
            anchorKey: blocks.first().get('key'),
            anchorOffset: 0,
            focusKey: blocks.last().get('key'),
            focusOffset: blocks.last().getLength(),
          });
          const newContentState = removeRange(
            editorState.getCurrentContent(),
            updatedSelection,
            'forward'
          );

          return push(es, newContentState, 'remove-range');
        };
        setEditorState(clearEditorContent(editorState));
      },
      // ref.current.insertText(text) will insert the text at the end of the input
      insert(text) {
        const contentState = editorState.getCurrentContent();
        const blockMap = contentState.getBlockMap();
        const key = blockMap.last().getKey();
        const length = blockMap.last().getLength();
        const selection = new SelectionState({
          anchorKey: key,
          anchorOffset: length,
          focusKey: key,
          focusOffset: length,
        });
        const newContentState = insertText(contentState, selection, `${text} `);
        const newEditorState = push(editorState, newContentState, 'insert-characters');
        handleOnChange(newEditorState);
      },
      focus() {
        editorRef.current.focus();
      },
      // ref.current.removeCarriageReturn() will remove the last carriage return
      removeCarriageReturn() {
        const currentContent = editorState.getCurrentContent();
        const newContent = currentContent.getBlockMap().reduce((accumulator, block) => {
          const key = block.getKey();
          const next = currentContent.getBlockAfter(key);
          if (block && next && next.getText().length === 0) {
            const removeSelection = new SelectionState({
              anchorKey: key,
              anchorOffset: block.getText().length,
              focusKey: next.getKey(),
              focusOffset: 0,
            });

            return removeRange(
              currentContent,
              removeSelection,
              'forward'
            );
          }

          return accumulator;
        }, currentContent);
        const newEditorState = push(editorState, newContent, 'remove-range');
        handleOnChange(newEditorState);
      },
      // ref.current.trim() will trim whitespace
      // https://stackoverflow.com/a/58873446
      trim() {
        const currentContent = editorState.getCurrentContent();
        const newContent = currentContent.getBlockMap().reduce((accumulator, block) => {
          const key = block.getKey();
          const text = block.getText();
          const trimmedLeft = text.trimLeft();
          const trimmedRight = text.trimRight();
          const offset = text.length - trimmedLeft.length;
          const textToReplaceLeft = new SelectionState({
            anchorKey: key,
            focusKey: key,
            anchorOffset: 0,
            focusOffset: offset,
          });
          const leftTrimmedContent = replaceText(accumulator, textToReplaceLeft, '');
          const textToReplaceRight = new SelectionState({
            anchorKey: key,
            focusKey: key,
            anchorOffset: trimmedRight.length - offset,
            focusOffset: text.length - offset,
          });

          return replaceText(leftTrimmedContent, textToReplaceRight, '');
        }, currentContent);
        const newEditorState = push(editorState, newContent, 'remove-range');
        handleOnChange(newEditorState);
      },
    };
  }, [editorState, handleOnChange]);

  const [openMentionsSuggestions, setOpenMentionsSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState(mentionsList);
  const mentionStyleClasses = mentionStyle({ multiline });
  const { MentionSuggestions, plugins } = useMemo(() => {
    const mentionPlugin = createMentionPlugin({
      entityMutability: 'IMMUTABLE',
      theme: mentionStyleClasses,
      supportWhitespace: true,
      popperOptions: {
        strategy: 'absolute',
        placement: 'top',
      },
    });
    const { MentionSuggestions: MentionSuggestions2 } = mentionPlugin;
    const plugins2 = [mentionPlugin];

    return { plugins: plugins2, MentionSuggestions: MentionSuggestions2 };
  }, [mentionStyleClasses]);

  const onOpenMentionsSuggestions = useCallback((open) => {
    setOpenMentionsSuggestions(open);
  }, []);

  const onSearchMentionsSuggestionsChange = useCallback(({ value: value2 }) => {
    setSuggestions(customSuggestionsFilter(value2, mentionsList));
  }, [mentionsList]);

  const getLengthOfSelectedText = useCallback(() => {
    const currentSelection = editorState.getSelection();
    const isCollapsed = currentSelection.isCollapsed();
    let length = 0;
    if (!isCollapsed) {
      const currentContent = editorState.getCurrentContent();
      const startKey = currentSelection.getStartKey();
      const endKey = currentSelection.getEndKey();
      const startBlock = currentContent.getBlockForKey(startKey);
      const isStartAndEndBlockAreTheSame = startKey === endKey;
      const startBlockTextLength = startBlock.getLength();
      const startSelectedTextLength = startBlockTextLength - currentSelection.getStartOffset();
      const endSelectedTextLength = currentSelection.getEndOffset();
      const keyAfterEnd = currentContent.getKeyAfter(endKey);
      if (isStartAndEndBlockAreTheSame) {
        length += currentSelection.getEndOffset() - currentSelection.getStartOffset();
      } else {
        let currentKey = startKey;
        while (currentKey && currentKey !== keyAfterEnd) {
          if (currentKey === startKey) {
            length += startSelectedTextLength + 1;
          } else if (currentKey === endKey) {
            length += endSelectedTextLength;
          } else {
            length += currentContent.getBlockForKey(currentKey).getLength() + 1;
          }
          currentKey = currentContent.getKeyAfter(currentKey);
        }
      }
    }

    return length;
  }, [editorState]);
  const MAX_LENGTH = originalInputInputProps?.maxLength;

  const handleBeforeInput = useCallback(() => {
    if (!MAX_LENGTH) {
      return;
    }
    const currentContentLength = editorState.getCurrentContent().getPlainText('').length;
    const selectedTextLength = getLengthOfSelectedText();
    if (currentContentLength - selectedTextLength > MAX_LENGTH - 1) {
      return 'handled';
    }
  }, [editorState, getLengthOfSelectedText, MAX_LENGTH]);

  const handlePastedText = useCallback((pastedText) => {
    if (!MAX_LENGTH) {
      return;
    }
    const currentContentLength = editorState.getCurrentContent().getPlainText('').length;
    const selectedTextLength = getLengthOfSelectedText();
    if (currentContentLength + pastedText.length - selectedTextLength > MAX_LENGTH) {
      const contentState = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      const pasteLimit = MAX_LENGTH - currentContentLength;
      const truncatedText = pastedText.substring(0, pasteLimit);
      if (truncatedText.length + currentContentLength <= MAX_LENGTH) {
        const newContentState = replaceText(contentState, selection, `${truncatedText}`);
        const newEditorState = push(editorState, newContentState, 'insert-characters');
        handleOnChange(newEditorState);
      }

      return 'handled';
    }
  }, [editorState, getLengthOfSelectedText, MAX_LENGTH, handleOnChange]);

  const handleReturn = useCallback(() => {
    if (openMentionsSuggestions && onKeyUp || !multiline) {
      return 'handled';
    }

    return 'not-handled';
  }, [multiline, onKeyUp, openMentionsSuggestions]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      width={selectiveProps.fullWidth ? "100%" : "auto"}
      height="inherit"
    >
      <TextField
        {...selectiveProps}
        className="text-field-mention"
        onKeyUp={(e) => {
          if (onKeyUp && !openMentionsSuggestions) {
            onKeyUp(e);
          }
        }}
        InputLabelProps={{ shrink: true }}
        onChange={onChange}
        InputProps={{
          inputProps: {
            component: Editor,
            editorRef,
            editorState,
            handleOnChange,
            handleBeforeInput,
            handlePastedText,
            handleReturn,
            plugins,
            ...originalInputInputProps,
          },
          inputComponent: DraftField,
          ...originalInputProps,
        }}
      />
      <Box textAlign="left" style={{ color: "grey" }} ml={1.7}>
        <Typography
          variant="subtitle2"
          color="inherit"
          style={{ fontSize: "1.2rem" }}
        >
          {mentionHelperText}
        </Typography>
      </Box>
      <MentionSuggestions
        open={openMentionsSuggestions}
        onOpenChange={onOpenMentionsSuggestions}
        suggestions={suggestions}
        onSearchChange={onSearchMentionsSuggestionsChange}
        entryComponent={MentionEntry}
      />
    </Box>
  );
};

export default DraftEditor;
